import { Component, type ErrorInfo, type ReactNode } from 'react';
import { darkTheme } from '../../lib/tokens';

/**
 * Error-Boundary um `/social/*` (CUR-326).
 *
 * **Warum ausgerechnet hier.** Das Dashboard hat heute keine einzige — ein
 * Render-Fehler weißt die komplette Anwendung, und man sieht nicht einmal, wo
 * er herkam. Der Hub bringt neue Datenformen mit, die kein anderer Screen hat:
 * `Json`-Felder direkt aus dem Manifest (`settings`, `checklist`, `notes`),
 * fehlende Thumbnails, Kanalfassungen, die es nicht gibt. Das ist die Stelle,
 * an der so etwas zuerst auftritt.
 *
 * Der Schutz gehört deshalb hierhin, statt darauf zu warten, dass es jemanden
 * trifft — und nicht global, weil eine globale Boundary eine eigene
 * Entscheidung mit eigenen Folgen wäre (sie verschluckt dann auch Fehler, die
 * man lieber roh sähe).
 */
interface Props {
  children: ReactNode;
}

interface State {
  fehler: Error | null;
}

export class SocialErrorBoundary extends Component<Props, State> {
  override state: State = { fehler: null };

  static getDerivedStateFromError(fehler: Error): State {
    return { fehler };
  }

  override componentDidCatch(fehler: Error, info: ErrorInfo): void {
    // In die Konsole, nicht in ein Telemetrie-Ziel: Das Dashboard hat keines,
    // und ein Werkzeug für eine Person braucht keine Fehler-Pipeline — es
    // braucht, dass der Stack auffindbar bleibt.
    console.error('[social] Render-Fehler:', fehler, info.componentStack);
  }

  override render(): ReactNode {
    const { fehler } = this.state;
    if (!fehler) return this.props.children;

    const t = darkTheme.colors;
    return (
      <div className="mx-auto max-w-[760px] p-6">
        <h1 className="mb-2 text-xl font-bold" style={{ color: t.textPrimary }}>
          Der Social-Bereich konnte nicht gezeichnet werden
        </h1>
        <p className="mb-4 text-sm" style={{ color: t.textSecondary }}>
          Der Rest des Dashboards läuft weiter — nur diese Seite ist betroffen.
        </p>
        <pre
          className="mb-4 overflow-x-auto rounded-md border p-3 text-xs"
          style={{ borderColor: t.borderSubtle, backgroundColor: t.bgSurface, color: t.danger }}
        >
          {fehler.message}
        </pre>
        <button
          type="button"
          onClick={() => this.setState({ fehler: null })}
          className="min-h-[44px] rounded-lg px-4 font-semibold text-white"
          style={{ backgroundColor: t.accent }}
        >
          Nochmal versuchen
        </button>
      </div>
    );
  }
}
